import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  documentId,
  FieldPath,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch
} from 'firebase/firestore';
import { getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import { auth, db, PROJECT_ID, storage, storageReference } from '.';
import { getHostedBookingsByVenue, isAnyLocation, isAvailable } from '../components/helpers';
import { BOOKER, HOST, byStartNewestFirst, readBookings } from './bookings';
import { COLLECTION } from '../helper/collections';
import { getAllSundaysOpen } from './settings';

const PAGE_SIZE = 20;
const MAX_PAGES = 10;
const MAX_RESULTS = MAX_PAGES * PAGE_SIZE;

// During photo upload, photos are resized for the web. If we ever
// want to change the web size, we can adjust the Firebase extension
// and new resized images are generated. Consequently, we'd prefer
// to keep the venue's `photos` array as the original photo's filepath,
// and then dynamically adjust the photo's filepath to whatever path
// is the latest extension settings.
const RESIZE_SETTING = '_1000x1000';
const getPhotosForWeb = (photos) => {
  if (!Array.isArray(photos) || !photos.length) {
    return [];
  }
  return photos.map((photo) => {
    const url = new URL(photo.url);
    url.pathname += RESIZE_SETTING;
    const thumbnail = url.toString();
    return {
      ...photo,
      thumbnail
    };
  });
};

export const geoCode = async (location) => {
  const locationEncoded = location.replace(' ', '+');

  const urlProduction = `https://us-central1-${PROJECT_ID}.cloudfunctions.net/geoCode`;
  const urlLocalhost = `http://127.0.0.1:5001/${PROJECT_ID}/us-central1/geoCode`;
  let url = '';
  if (['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    url = urlLocalhost;
  } else {
    url = urlProduction;
  }

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ location: locationEncoded })
  });
  const data = await res.json();

  if (res.status !== 200) {
    throw new Error(`${data.error}: Error al obtener coordenadas.`);
  }

  return [Number(data.lat), Number(data.lng), data.exactMatch];
};

// A paused venue (host clicked "Pause Venue": `disabled`) never shows in search.
// isAvailable also checks it, but only runs when the search has dates; a search
// with no dates showed paused venues (PR #336).
export const isListed = (venue) => !venue?.disabled;

export const startSearchResults = async ({ setFetching, setSearchResults, location, numGuests, startDate, endDate }) => {
  setFetching(true);
  let search = collection(db, COLLECTION.VENUES);
  search = query(search, where('verified', '==', true));

  const DISTANCE_DELTA = 0.25; // 35 mile circumference from search coordinates. ("1" is 138 mile circumference)
  const hasDefinedLocation = !isAnyLocation(location);
  let longitude, latitude;
  if (hasDefinedLocation) {
    try {
      const [_latitude, _longitude] = await geoCode(location);
      longitude = Number(_longitude);
      latitude = Number(_latitude);
      search = query(search, where('location.lat', '>=', latitude - DISTANCE_DELTA));
      search = query(search, where('location.lat', '<=', latitude + DISTANCE_DELTA));
    } catch (e) {
      console.error(e);
    }
  }

  numGuests = Number(numGuests);

  const hasDefinedDate = startDate !== 'none' && endDate !== 'none';

  search = query(search, limit(MAX_RESULTS));
  getDocs(search).then(async (venuesSnapshot) => {
    const allSundaysOpen = hasDefinedDate ? await getAllSundaysOpen() : false;
    let venues =
      venuesSnapshot?.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      })) || [];

    venues = venues.filter(
      (venue) =>
        isListed(venue) &&
        // location match
        (!hasDefinedLocation || (venue.location.lng >= longitude - DISTANCE_DELTA && venue.location.lng <= longitude + DISTANCE_DELTA)) &&
        // guest match
        (!numGuests || venue.maxGuests >= numGuests) &&
        // date-availability match
        (!hasDefinedDate || isAvailable({ startDate, endDate }, venue, allSundaysOpen))
    );

    // Sort by distance from searched location
    venues.forEach((venue) => {
      venue.distance = Math.abs(longitude - venue.location.lng);
      venue.distance += Math.abs(latitude - venue.location.lat);
      return venue;
    });
    venues.sort((a, b) => {
      if (a.distance < b.distance) {
        return -1;
      }
      if (a.distance > b.distance) {
        return 1;
      }
      return 0;
    });

    // Add thumbnails
    venues.forEach((venue) => {
      venue.photos = getPhotosForWeb(venue.photos);
    });

    setSearchResults({
      venues,
      pageSize: PAGE_SIZE,
      totalPages: Math.ceil(venues.length / PAGE_SIZE),
      hasResults: venues.length > 0
    });

    setTimeout(() => {
      setFetching(false);
    }, 10);
  });
};

export const startFavoritedResults = async ({ setFetching, setSearchResults, user }) => {
  setFetching(true);

  const { savedVenues } = user;

  if (!Array.isArray(savedVenues) || !savedVenues.length) {
    setSearchResults({
      venues: [],
      pageSize: PAGE_SIZE,
      totalPages: 0,
      hasResults: false
    });
    setTimeout(() => setFetching(false), 10);
    return;
  }

  const q = query(collection(db, 'venues'), where('verified', '==', true), where(documentId(), 'in', savedVenues.slice(0, 10)), limit(MAX_RESULTS));

  const venuesSnapshot = await getDocs(q);

  const venues =
    venuesSnapshot?.docs?.map((doc) => ({
      id: doc.id,
      ...doc.data()
    })) || [];

  venues.forEach((venue) => {
    venue.photos = getPhotosForWeb(venue.photos);
  });

  setSearchResults({
    venues,
    pageSize: PAGE_SIZE,
    totalPages: Math.ceil(venues.length / PAGE_SIZE),
    hasResults: venues.length > 0
  });

  setTimeout(() => setFetching(false), 10);
};

export const startReservedResults = async ({ setFetching, setSearchResults, user }) => {
  setFetching(true);

  // Billing B3.0a: from the `bookings` records.
  const bookings = (await readBookings(BOOKER, user && user.id)).filter(
    (booking) => booking.stripe_id && booking.venue_id && booking.amount && booking.start && booking.end
  );

  // output a new collection, "reservations", from venue object + booking object; use stripe ID (since venues can be booked multiple times)
  if (!Array.isArray(bookings) || !bookings.length) {
    setSearchResults({
      venues: [],
      pageSize: PAGE_SIZE,
      totalPages: 0,
      hasResults: false
    });
    setTimeout(() => {
      setFetching(false);
    }, 10);
    return;
  }

  const venueIds = bookings.map((booking) => booking.venue_id);

  const venuesQuery = query(
    collection(db, COLLECTION.VENUES),
    where(new FieldPath('__name__'), 'in', venueIds), // Limit to MAX_RESULTS to avoid exceeding Firestore limits)
    limit(MAX_RESULTS)
  );

  const venuesSnapshot = await getDocs(venuesQuery);

  const venues = venuesSnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data()
  }));

  const reservations = bookings
    .map((booking) => {
      const venue = venues.find((venue) => venue.id === booking.venue_id);
      if (!venue) return null;
      const photos = getPhotosForWeb(venue.photos);
      return {
        ...venue,
        photos,
        booking,
        venue_id: venue.id,
        id: booking.stripe_id
      };
    })
    .filter(Boolean);

  reservations.sort(byStartNewestFirst);

  setSearchResults({
    venues: reservations,
    pageSize: PAGE_SIZE,
    totalPages: Math.ceil(reservations.length / PAGE_SIZE),
    hasResults: reservations.length > 0
  });

  setTimeout(() => setFetching(false), 10);
};

export const startHostReservedResults = async ({ setFetching, setSearchResults, user }) => {
  setFetching(true);

  // Billing B3.0a: from the `bookings` records.
  const hostedBookings = await readBookings(HOST, user && user.id);
  // if user has no bookings
  if (!hostedBookings.length) {
    setSearchResults({
      venues: [],
      pageSize: PAGE_SIZE,
      totalPages: 0,
      hasResults: false
    });
    setTimeout(() => setFetching(false), 10);
    return;
  }

  const hostedBookingsByVenue = getHostedBookingsByVenue(hostedBookings);

  const venuesQuery = query(collection(db, 'venues'), where('userId', '==', auth.currentUser.uid));

  const venuesSnapshot = await getDocs(venuesQuery);

  const venues = (
    venuesSnapshot?.docs?.map((doc) => ({
      id: doc.id,
      ...doc.data()
    })) || []
  ).filter((venue) => hostedBookingsByVenue[venue.id]);

  const reservations = venues.reduce(
    (reservations, venue) => [
      ...reservations,
      ...hostedBookingsByVenue[venue.id].map((booking) => {
        const photos = getPhotosForWeb(venue.photos);
        return {
          ...venue,
          photos,
          booking: { ...booking },
          totalBookings: hostedBookingsByVenue[venue.id].length,
          venue_id: venue.id,
          id: booking.stripe_id
        };
      })
    ],
    []
  );

  reservations.sort(byStartNewestFirst);

  setSearchResults({
    venues: reservations,
    pageSize: PAGE_SIZE,
    totalPages: Math.ceil(reservations.length / PAGE_SIZE),
    hasResults: reservations.length > 0
  });

  setTimeout(() => setFetching(false), 10);
};

export const startHostedResults = async ({ setFetching, setSearchResults, user }) => {
  setFetching(true);

  // Billing B3.0a: from the `bookings` records.
  const hostedBookingsByVenue = getHostedBookingsByVenue(await readBookings(HOST, user.id));

  const venuesQuery = query(collection(db, 'venues'), where('userId', '==', user.id));

  const venuesSnapshot = await getDocs(venuesQuery);

  const venues =
    venuesSnapshot?.docs?.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      totalBookings: (hostedBookingsByVenue[doc.id] || []).length
    })) || [];

  // Add thumbnails
  venues.forEach((venue) => {
    venue.photos = getPhotosForWeb(venue.photos);
  });

  setSearchResults({
    venues,
    pageSize: PAGE_SIZE,
    totalPages: Math.ceil(venues.length / PAGE_SIZE),
    hasResults: venues.length > 0
  });

  setTimeout(() => {
    setFetching(false);
  }, 10);
};

export const startHasHostedVenues = async ({ setHasVenues }) => {
  const user = auth.currentUser;
  if (!user) {
    console.warn('There is no authenticated user for hasVenues');
    setHasVenues(false);
    return;
  }

  let search = collection(db, COLLECTION.VENUES);

  search = query(search, where('userId', '==', user.uid));

  try {
    const snapshot = await getDocs(search);
    setHasVenues(!snapshot.empty);
  } catch (err) {
    console.error('Error checking hosted venues:', err);
    setHasVenues(false);
  }
};

// Venue-search VS1.3: who changed a venue, and when (DS-V4, DS-C1). Every venue
// write from the browser carries it; firestore.rules refuses a write without it.
export const changeStamp = (role) => ({ updatedAt: serverTimestamp(), updatedBy: { role, userId: auth.currentUser.uid } });

// "admin" for an admin account (the token's admin claim), else "host". The rules check it.
export const currentRole = async () => ((await auth.currentUser.getIdTokenResult()).claims.admin === true ? 'admin' : 'host');

export const venuePrivateRef = (hostId, venueId) => doc(db, COLLECTION.USERS, hostId, 'venuePrivate', venueId);

// The host's private record for one venue (on-site contact, requirements), or null.
export const getVenuePrivate = async (hostId, venueId) => {
  if (!hostId || !venueId) return null;
  const snapshot = await getDoc(venuePrivateRef(hostId, venueId));
  return snapshot.exists() ? snapshot.data() : null;
};

// Saves the venue and its private record together: both, or neither.
// buildProps / buildPrivate get the Firestore helpers, so venueFacts.js stays plain.
export const saveHostedVenue = async ({ id, hostId, buildProps, buildPrivate }) => {
  const create = !id;
  const role = await currentRole();
  const venueRef = create ? doc(collection(db, COLLECTION.VENUES)) : doc(db, COLLECTION.VENUES, id);
  const now = serverTimestamp();
  const props = { ...buildProps({ create, remove: deleteField() }), ...changeStamp(role) };
  if (create) props.createdAt = now;
  const batch = writeBatch(db);
  if (create) batch.set(venueRef, props);
  else batch.update(venueRef, props);
  batch.set(venuePrivateRef(hostId, venueRef.id), buildPrivate({ now, role }), { merge: true });
  await batch.commit();
  return venueRef.id;
};

export const addVenuePhoto = async ({ photo, onProgress }, onSuccess, onError) => {
  if (!photo.name) {
    onError('Photo is missing a filename');
    return;
  }
  // A new random name for every upload, so an upload never replaces an earlier photo.
  // Not the original file name: it can hold private facts (an address) or text
  // written for an AI, and it would be public in the photo link.
  // Nested in the user's ID, so we can more easily clean up when users abuse file upload.
  const randomName = Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('');
  const path = `photos/${auth.currentUser.uid}/${randomName}`;

  // 2. Crea la referencia al archivo
  const fileRef = storageReference(storage, path);

  // 3. Inicia la subida
  const uploadTask = uploadBytesResumable(fileRef, photo);

  uploadTask.on(
    'state_changed',
    (snapshot) => onProgress && snapshot.totalBytes && onProgress(snapshot.bytesTransferred / snapshot.totalBytes),
    () => onError(),
    async () => {
      try {
        // Get the download URL correctly
        const rawUrl = await getDownloadURL(uploadTask.snapshot.ref);

        // Clean up URL string
        const urlParsed = new URL(rawUrl);
        urlParsed.searchParams.delete('token');
        const url = urlParsed.toString();

        // Pass the proper path reference and URL
        onSuccess({ ref: path, url });
      } catch (e) {
        console.error(e);
        onError(e);
      }
    }
  );
};

export const getVenue = async ({ id, setVenue, setFetching }) => {
  if (!id) {
    setVenue(null);
    setFetching(false);
    return;
  }

  try {
    const docRef = doc(db, COLLECTION.VENUES, id); // ✅ Correcta referencia de documento
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const venueObject = {
        id: docSnap.id,
        ...docSnap.data()
      };
      venueObject.photos = getPhotosForWeb(venueObject.photos);
      setVenue(venueObject);
    } else {
      console.warn('No such venue!');
      setVenue(null);
    }
  } catch (error) {
    console.error('Error getting venue data:', error);
    alert('There was an error getting venue data');
  } finally {
    setFetching(false);
  }
};

export const startSetHostedVenues = async (setHostedVenues, setFetching) => {
  const user = auth.currentUser;
  if (!user) {
    setHostedVenues([]);
    setFetching(false);
    return;
  }

  setHostedVenues([]);
  setFetching(true);

  try {
    const venuesCol = collection(db, COLLECTION.VENUES);

    const q = query(venuesCol, where('userId', '==', user.uid));

    const snapshot = await getDocs(q);

    const venues = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }));

    setHostedVenues(venues);
  } catch (err) {
    console.error('Error fetching hosted venues:', err);
    // you might want to notify the user here
  } finally {
    // always hide spinner
    setFetching(false);
  }
};

export const deleteVenue = async (venueId, setHostedVenues, hostedVenues) => {
  try {
    await deleteDoc(doc(db, COLLECTION.VENUES, venueId));
    setHostedVenues(hostedVenues.filter((venue) => venue.id !== venueId));
  } catch (err) {
    console.error(err);
    onError(err);
  }
};

export const getListVenues = async (venueIds, setVenues, setFetching) => {
  try {
    // 1. Para cada ID, creamos un getDoc(...) con doc(db,'venues', id)
    const snaps = await Promise.all(venueIds.map((id) => getDoc(doc(db, COLLECTION.VENUES, id))));

    console.log('🚀 ~ getListVenues ~ snaps:', snaps);

    // 2. Filtramos los que existen y convertimos en objetos
    const venues = snaps
      .filter((snap) => snap.exists())
      .map((snap) => ({
        id: snap.id,
        ...snap.data()
      }));

    // 3. Actualizamos el estado una sola vez, cuando ya tenemos todo
    setVenues(venues);
  } catch (error) {
    console.error('Error fetching venues list:', error);
    // Aquí podrías llamar a un onError() o mostrar alerta si quieres
  } finally {
    // 4. Quitamos el loading
    setFetching(false);
  }
};

export const getUserRSVenues = async (setSavedVenues, setReservedVenues, setFetching) => {
  const user = auth.currentUser;

  try {
    // 1. Load the user document
    const userRef = doc(db, COLLECTION.USERS, user.uid);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) throw new Error('User not found');

    const { savedVenues = [] } = userSnap.data();
    // 2. Fetch saved venues list
    await getListVenues(savedVenues, setSavedVenues, () => {});

    // 3. Query “events” where the current user is guest
    const eventsQ = query(collection(db, 'events'), where('guestId', '==', user.uid));
    const eventsSnap = await getDocs(eventsQ);
    const reservedIds = eventsSnap.docs.map((d) => d.data().venueId);

    // 4. Fetch reserved venues list
    await getListVenues(reservedIds, setReservedVenues, setFetching);
  } catch (err) {
    console.error('Error getting user RS venues:', err);
    onError(err);
  }
};

export const saveEmails = async (email) => {
  const urlProduction = `https://us-central1-${PROJECT_ID}.cloudfunctions.net/saveEmails`;
  const urlLocalhost = `http://127.0.0.1:5001/${PROJECT_ID}/us-central1/saveEmails`;
  let url = '';
  if (['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    url = urlLocalhost;
  } else {
    url = urlProduction;
  }

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email: email })
  });
  const data = await res.json();

  if (res.status !== 200) {
    throw new Error(`${data.error}: no se pudo guardar el correo electrónico.`);
  }

  return data;
};
